import clsx from 'clsx';
import React from 'react';
import { Link } from 'wouter';

import { TILE_BORDER } from './constants';

const tileClassName = clsx(
  'group flex flex-col items-start justify-start w-full p-f1 aspect-[8/9] overflow-hidden hover:bg-negative cursor-pointer',
  TILE_BORDER
);

type GridTileProps = {
  icon: React.ReactNode;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  'data-testid'?: string;
};

/**
 * Shared tile component for payments grid views.
 * Layout: icon → gap-y-f2 → primary title + secondary info (no gap between them).
 */
export const GridTile = ({
  icon,
  primary,
  secondary,
  onClick,
  href,
  'data-testid': dataTestId,
}: GridTileProps): JSX.Element => {
  const content = (
    <>
      {icon}
      <div className="mt-f2 w-full overflow-hidden">
        <span className="text-main text-xmd truncate w-full block text-left">
          {primary}
        </span>
        {secondary && (
          <span className="text-main-500 text-xmd truncate w-full block text-left">
            {secondary}
          </span>
        )}
      </div>
    </>
  );

  if (href) {
    return (
      <Link href={href}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a className={tileClassName} data-testid={dataTestId}>
          {content}
        </a>
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={tileClassName}
      data-testid={dataTestId}
    >
      {content}
    </button>
  );
};

type EmptyGridTileProps = {
  icon: React.ReactNode;
  label: string;
};

export const EmptyGridTile = ({
  icon,
  label,
}: EmptyGridTileProps): JSX.Element => (
  <div
    className={clsx(
      'flex flex-col items-start justify-start w-full p-f1 aspect-[8/9] overflow-hidden',
      TILE_BORDER
    )}
  >
    {icon}
    <div className="mt-f2 w-full overflow-hidden">
      <span className="text-main-500 text-xmd">{label}</span>
    </div>
  </div>
);
